import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Blocks, Armchair, Flower2, Sprout, TreePine, Scissors, ArrowRight } from "lucide-react";

const ServiceCard = ({ service, index }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "center center", "end start"]
  });

  const imageHeight = useTransform(scrollYProgress, [0, 0.4, 0.6, 1], ["12rem", "24rem", "24rem", "12rem"]);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1, duration: 0.5 }}
      className="service-card group"
      data-testid={`service-card-${index}`}
    >
      {/* Image */}
      <motion.div
        style={{ height: imageHeight }}
        className="relative -mx-8 -mt-8 mb-6 overflow-hidden rounded-t-xl"
      >
        <img
          src={service.image}
          alt={service.title}
          className="w-full h-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
      </motion.div>

      {/* Icon */}
      <div className="icon-wrapper">
        <service.icon className="w-7 h-7 text-[#1A4D2E] transition-colors duration-300" />
      </div>

      {/* Content */}
      <h3 className="font-heading text-xl font-bold text-[#1A4D2E] mb-3">
        {service.title}
      </h3>
      <p className="text-gray-600 leading-relaxed flex-grow">
        {service.description}
      </p>

      {/* Link */}
      <a
        href="#contact"
        className="inline-flex items-center gap-2 text-[#8B5A2B] font-semibold mt-4 hover:text-[#1A4D2E] transition-colors group/link"
      >
        Get Quote
        <ArrowRight className="w-4 h-4 transition-transform group-hover/link:translate-x-1" />
      </a>
    </motion.div>
  );
};

const ServicesSection = () => {
  const services = [
    {
      icon: Blocks,
      title: "Driveways",
      description: "Block paving, tarmac, gravel and resin driveways built to last. We handle everything from design to completion for a stunning entrance to your home.",
      image: "https://customer-assets.emergentagent.com/job_21ac811b-a248-4ccc-b1af-d39168c4c5ba/artifacts/8a4l7c2t_22.jpeg"
    },
    {
      icon: Armchair,
      title: "Patios",
      description: "Beautiful patio areas for outdoor living. From natural stone slabs to porcelain tiles, we create the perfect space to enjoy your garden.",
      image: "https://customer-assets.emergentagent.com/job_21ac811b-a248-4ccc-b1af-d39168c4c5ba/artifacts/6c4wisos_23.jpeg"
    },
    {
      icon: Flower2,
      title: "Garden Design",
      description: "Complete garden transformations — from turfing and planting to fencing, decking, patios and water features. We bring your outdoor vision to life.",
      image: "https://customer-assets.emergentagent.com/job_21ac811b-a248-4ccc-b1af-d39168c4c5ba/artifacts/o0xb1x01_p.jpeg"
    },
    {
      icon: Sprout,
      title: "Grounds Maintenance",
      description: "Regular contract or one-off grounds maintenance, land clearance and site preparation for commercial and residential properties.",
      image: "https://customer-assets.emergentagent.com/job_21ac811b-a248-4ccc-b1af-d39168c4c5ba/artifacts/07xvle29_new5.jpg"
    },
    {
      icon: TreePine,
      title: "Tree Surgery",
      description: "Professional tree removal, pruning, crown reduction and stump grinding. Safe, qualified work for trees of all sizes.",
      image: "https://customer-assets.emergentagent.com/job_21ac811b-a248-4ccc-b1af-d39168c4c5ba/artifacts/xgzs9xko_w1.jpeg"
    },
    {
      icon: Scissors,
      title: "Hedge Cutting & Tree Felling",
      description: "Professional hedge trimming, shaping and tree felling to keep your boundaries neat and remove unwanted trees safely.",
      image: "https://customer-assets.emergentagent.com/job_21ac811b-a248-4ccc-b1af-d39168c4c5ba/artifacts/u95grnrc_WhatsApp%20Image%202026-04-21%20at%2019.48.28.jpeg"
    },
  ];

  return (
    <section id="services" className="py-16 md:py-24 lg:py-32 bg-white" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <span className="text-sm font-medium tracking-wide uppercase text-[#8B5A2B] mb-4 block">
            What We Do
          </span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-[#1A4D2E] mb-6">
            Our Services
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            From driveways and patios to garden design, grounds maintenance and tree surgery — we provide a full range of landscaping services across Northumberland.
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <ServiceCard key={index} service={service} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
